import dayjs from 'dayjs';
import { useState } from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { Tag, Image, Modal, Drawer, Typography, numberFormatting } from 'design-system';

import type { ActionPlan } from 'src/services/actionPlan';
import { ActionPlanStatus } from 'src/services/actionPlan';

// ProgressBar组件
interface ProgressBarProps {
  percent: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ percent }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ marginRight: 8, minWidth: '50px' }}>{percent}%</span>
    <div style={{ width: '100px', position: 'relative' }}>
      <div
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#ebeff7',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percent > 100 ? 100 : percent}%`,
            height: '100%',
            backgroundColor: percent >= 100 ? '#52c41a' : '#105FC8',
            borderRadius: '4px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  </div>
);

interface Props {
  open: boolean;
  onClose: () => void;
  data: ActionPlan | null;
}

// 状态映射
const statusMap = {
  [ActionPlanStatus.NotStarted]: { text: '--', color: 'default' },

  [ActionPlanStatus.Completed]: { text: '已完成', color: 'success' },
};

// 考核判断维度映射
const assessmentDimensionMap = {
  Duration: '按时间',
  Quantity: '按数量',
  Boolean: '是与否',
};

// 获取详细的进度状态信息
const getDetailedProgressStatus = (record: ActionPlan) => {
  const { status, assessmentDimension, actualEndDate, planEndDate, metadata } = record;
  const currentStatus = status || ActionPlanStatus.NotStarted;

  // 如果 status 为 null，不管 assessmentDimension 是什么，都显示 "--"
  if (status === null) {
    return { text: '--', color: 'default' };
  }
  if (assessmentDimension === 'Quantity' && currentStatus === ActionPlanStatus.Completed) {
    return { text: '--', color: 'default' };
  }

  // 当 assessmentDimension != 'Quantity' 且 status == 'NotStarted' 时，显示"未完成"
  if (assessmentDimension !== 'Quantity' && currentStatus === ActionPlanStatus.NotStarted) {
    return { text: '未完成', color: 'error' };
  }

  // 如果状态不是已完成，直接返回原状态
  if (currentStatus !== ActionPlanStatus.Completed) {
    const statusInfo = statusMap[currentStatus];
    return { text: statusInfo.text, color: statusInfo.color };
  }

  // 如果是已完成状态，且assessmentDimension为Duration，则检查时间完成情况
  if (assessmentDimension === 'Duration' && actualEndDate && planEndDate) {
    const actualEnd = dayjs(actualEndDate);
    const planEnd = dayjs(planEndDate);

    if (actualEnd.isBefore(planEnd)) {
      return { text: '提前完成', color: 'success' };
    }
    if (actualEnd.isSame(planEnd, 'day')) {
      return { text: '按时完成', color: 'processing' };
    }
    return { text: '延后完成', color: 'warning' };
  }

  // 其他情况显示已完成
  return { text: '已完成', color: 'success' };
};

export default function ActionPlanDetail({ open, onClose, data }: Props) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');

  if (!data) return null;

  const statusInfo = getDetailedProgressStatus(data);

  // 获取文件扩展名
  const getFileExtension = (url: string) => {
    const match = url.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : '';
  };

  // 判断是否为图片文件
  const isImageFile = (url: string) => {
    const ext = getFileExtension(url);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  };

  // 判断是否为视频文件
  const isVideoFile = (url: string) => {
    const ext = getFileExtension(url);
    return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext);
  };

  // 判断是否为可预览的文档文件
  const isDocumentFile = (url: string) => {
    const ext = getFileExtension(url);
    return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(ext);
  };

  // 获取文件名
  const getFileName = (url: string, index: number) => {
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    return fileName || `附件${index + 1}`;
  };

  // 打开文档预览
  const handlePreviewDocument = (url: string, fileName: string) => {
    setPreviewUrl(url);
    setPreviewFileName(fileName);
    setPreviewOpen(true);
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>详情</span>
          {statusInfo && statusInfo.text !== '--' && (
            <Tag color={statusInfo.color} bordered={false}>
              {statusInfo.text}
            </Tag>
          )}
        </div>
      }
      width={600}
      open={open}
      onClose={onClose}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #f0f0f0',
          borderRadius: 2,
        }}
      >
        <div style={{ display: 'flex' }}>
          <div style={{ display: 'flex', flex: 1, borderRight: '1px solid #f0f0f0' }}>
            <div
              style={{
                width: '120px',
                padding: '12px 16px',
                color: '#666',
                background: '#F8F9FC',
                borderRight: '1px solid #f0f0f0',
              }}
            >
              考核部门
            </div>
            <div
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'white',
              }}
            >
              {data.department?.name || '-'}
            </div>
          </div>
          <div style={{ display: 'flex', flex: 1 }}>
            <div
              style={{
                width: '120px',
                padding: '12px 16px',
                color: '#666',
                background: '#F8F9FC',
                borderRight: '1px solid #f0f0f0',
              }}
            >
              责任人
            </div>
            <div
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'white',
              }}
            >
              {data.coordinators?.map((c) => `${c.lastName}${c.firstName}`).join('、') || '-'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
          <div
            style={{
              width: '120px',
              padding: '12px 16px',
              color: '#666',
              background: '#F8F9FC',
              borderRight: '1px solid #f0f0f0',
            }}
          >
            行动计划描述
          </div>
          <div
            style={{
              flex: 1,
              padding: '12px 16px',
              background: 'white',
            }}
          >
            {data.description || '-'}
          </div>
        </div>

        <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
          <div
            style={{
              width: '120px',
              padding: '12px 16px',
              color: '#666',
              background: '#F8F9FC',
              borderRight: '1px solid #f0f0f0',
            }}
          >
            计划任务周期
          </div>
          <div
            style={{
              flex: 1,
              padding: '12px 16px',
              background: 'white',
            }}
          >
            {data.planStartDate && data.planEndDate
              ? `${dayjs(data.planStartDate).format('YYYY-MM-DD')}至${dayjs(data.planEndDate).format('YYYY-MM-DD')}`
              : '-'}
          </div>
        </div>

        <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
          <div
            style={{
              width: '120px',
              padding: '12px 16px',
              color: '#666',
              background: '#F8F9FC',
              borderRight: '1px solid #f0f0f0',
            }}
          >
            考核判断维度
          </div>
          <div
            style={{
              flex: 1,
              padding: '12px 16px',
              background: 'white',
            }}
          >
            {data.assessmentDimension
              ? assessmentDimensionMap[data.assessmentDimension] || data.assessmentDimension
              : '-'}
          </div>
        </div>

        {/* 仅在数量维度时显示目标数量和实际完成数量 */}
        {data.assessmentDimension === 'Quantity' && (
          <>
            <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
              <div
                style={{
                  width: '120px',
                  padding: '12px 16px',
                  color: '#666',
                  background: '#F8F9FC',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                目标数量
              </div>
              <div
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'white',
                }}
              >
                {numberFormatting(data.metadata?.planQuantity || data.metadata?.planQuantity)}
              </div>
            </div>

            <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
              <div
                style={{
                  width: '120px',
                  padding: '12px 16px',
                  color: '#666',
                  background: '#F8F9FC',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                实际完成数量
              </div>
              <div
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'white',
                }}
              >
                {numberFormatting(data.metadata?.actualQuantity)}
              </div>
            </div>

            <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
              <div
                style={{
                  width: '120px',
                  padding: '12px 16px',
                  color: '#666',
                  background: '#F8F9FC',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                进度状态
              </div>
              <div
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'white',
                }}
              >
                {(() => {
                  const planQuantity =
                    data.metadata?.planQuantity || data.metadata?.planQuantity || 0;
                  const actualQuantity = data.metadata?.actualQuantity || 0;
                  const percent =
                    planQuantity > 0 ? Math.round((actualQuantity / planQuantity) * 100) : 0;

                  return <ProgressBar percent={percent} />;
                })()}
              </div>
            </div>
          </>
        )}

        <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
          <div
            style={{
              width: '120px',
              padding: '12px 16px',
              color: '#666',
              background: '#F8F9FC',
              borderRight: '1px solid #f0f0f0',
            }}
          >
            过程记录
          </div>
          <div
            style={{
              flex: 1,
              padding: '12px 16px',
              background: 'white',
            }}
          >
            {data.records?.description || '-'}
          </div>
        </div>
      </div>

      {data.records && data.records.attachments && data.records.attachments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Typography.Title level={5}>过程记录</Typography.Title>
          {data.records.description && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Text>{data.records.description}</Typography.Text>
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {data.records.attachments.map((url, index) => {
              const fileName = getFileName(url, index);

              const handlePreview = () => {
                if (isImageFile(url)) {
                  // 图片预览由 Image 组件自身处理，这里不需要额外逻辑
                } else if (isVideoFile(url)) {
                  // 视频文件直接在新窗口打开
                  window.open(url, '_blank');
                } else if (isDocumentFile(url)) {
                  handlePreviewDocument(url, fileName);
                } else {
                  // 其他文件类型直接下载
                  window.open(url, '_blank');
                }
              };

              const handleKeyDown = (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handlePreview();
                }
              };

              return (
                <div key={index} style={{ width: 200 }}>
                  {isImageFile(url) ? (
                    <div
                      style={{
                        width: 200,
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        backgroundColor: '#fafafa',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                        e.currentTarget.style.borderColor = '#d9d9d9';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Image
                        src={url}
                        alt={fileName}
                        width={200}
                        height={150}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        preview={{
                          src: url,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      style={{
                        width: 200,
                        height: 150,
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                        e.currentTarget.style.borderColor = '#d9d9d9';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      onClick={handlePreview}
                      onKeyDown={handleKeyDown}
                    >
                      {isVideoFile(url) && (
                        <>
                          <div style={{ fontSize: 24, marginBottom: 8 }}>🎬</div>
                          <Typography.Text style={{ textAlign: 'center', fontSize: 12 }}>
                            点击预览视频
                          </Typography.Text>
                        </>
                      )}
                      {isDocumentFile(url) && (
                        <>
                          <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                          <Typography.Text style={{ textAlign: 'center', fontSize: 12 }}>
                            点击预览文档
                          </Typography.Text>
                        </>
                      )}
                      {!isImageFile(url) && !isVideoFile(url) && !isDocumentFile(url) && (
                        <>
                          <div style={{ fontSize: 24, marginBottom: 8 }}>📎</div>
                          <Typography.Text style={{ textAlign: 'center', fontSize: 12 }}>
                            点击下载文件
                          </Typography.Text>
                        </>
                      )}
                    </div>
                  )}
                  <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    {fileName}
                  </Typography.Text>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 文档预览弹窗 */}
      <Modal
        title={null}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0, height: '80vh' }}
        closable
      >
        {previewUrl && (
          <div style={{ width: '100%', height: '100%' }}>
            {/* PDF 预览 */}
            {previewFileName.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={previewUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="PDF预览"
              />
            ) : /* DOCX、XLSX 使用 DocViewer 预览 */
            previewFileName.toLowerCase().endsWith('.docx') ||
              previewFileName.toLowerCase().endsWith('.doc') ||
              previewFileName.toLowerCase().endsWith('.xlsx') ||
              previewFileName.toLowerCase().endsWith('.xls') ? (
              <div
                style={{
                  width: '100%',
                  height: '80vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                className="excel-preview-container"
              >
                <DocViewer
                  documents={[
                    {
                      uri: previewUrl,
                      fileName: previewFileName || '文档预览',
                      fileType:
                        previewFileName.toLowerCase().endsWith('.xlsx') ||
                        previewFileName.toLowerCase().endsWith('.xls')
                          ? 'xlsx'
                          : previewFileName.toLowerCase().endsWith('.docx') ||
                              previewFileName.toLowerCase().endsWith('.doc')
                            ? 'docx'
                            : undefined,
                    },
                  ]}
                  pluginRenderers={DocViewerRenderers}
                  config={{
                    header: {
                      disableHeader: true,
                      disableFileName: true,
                      retainURLParams: false,
                    },
                    csvDelimiter: ',',
                  }}
                  style={{ width: '100%', height: '100%', display: 'block', flex: 1 }}
                />
              </div>
            ) : (
              /* 其他文档类型显示下载链接和基本信息 */
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>📎</div>
                <h3 style={{ marginBottom: '20px' }}>{previewFileName}</h3>
                <p style={{ color: '#666', marginBottom: '30px' }}>
                  此文件类型暂不支持在线预览，请下载后查看
                </p>
                <a
                  href={previewUrl}
                  download={previewFileName}
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    backgroundColor: '#1890ff',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '4px',
                  }}
                >
                  下载文件
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Drawer>
  );
}
